/**
 * Which drawn glyph a category name gets (`CategoryIcon`).
 *
 * The category list is **admin-curated in the database** (H7), not defined
 * here, and this branch has never been pointed at the real one. So matching on
 * exact strings was a bet: if the backend says "Dental clinic" where this code
 * says "Dentist", every tile silently falls back to the stethoscope and the
 * whole set looks broken for a reason nobody would guess from the screen.
 *
 * Matching on keywords removes the bet. "Dentist", "Dental", "Dentistry" and
 * "Dental clinic" all get the tooth, and a category nobody anticipated still
 * lands on a neutral clinical mark rather than nothing.
 *
 * Pure and I/O-free on purpose, so it is unit-tested rather than eyeballed.
 */

export type GlyphKey =
  | "tooth"
  | "chiro"
  | "physio"
  | "osteo"
  | "massage"
  | "psych"
  | "derm"
  | "nutrition"
  | "optician"
  | "general";

/**
 * Order matters: the first rule that matches wins. Specific specialties come
 * before the catch-all words ("clinic", "practice", "doctor"), so "Dental
 * clinic" is a dentist rather than a general practice.
 */
const RULES: { key: GlyphKey; keywords: string[] }[] = [
  {
    key: "tooth",
    keywords: ["dentist", "dental", "dentistry", "tooth", "teeth", "orthodont", "endodont", "periodont"],
  },
  {
    key: "chiro",
    keywords: ["chiroprac", "spine", "spinal"],
  },
  {
    key: "osteo",
    keywords: ["osteopath", "osteo"],
  },
  {
    key: "physio",
    keywords: ["physio", "physical therapy", "physiotherap", "rehab", "kinesi", "sports medicine"],
  },
  {
    // Before the psychology rule and after physio, so "Massage Therapist" and
    // "Physiotherapist" are never caught by a looser word for therapy.
    key: "massage",
    keywords: ["massage", "reflexolog"],
  },
  {
    // "therap" alone is deliberately absent: it is inside "Physiotherapist"
    // and "Massage Therapist", and order would be all that kept them apart.
    key: "psych",
    keywords: ["psycholog", "psychiatr", "psychotherap", "counsel", "mental health"],
  },
  {
    key: "derm",
    keywords: ["derma", "skin", "cosmetic", "aesthetic", "laser"],
  },
  {
    key: "nutrition",
    keywords: ["nutrition", "dietit", "dietet", "diet", "weight"],
  },
  {
    key: "optician",
    keywords: ["optic", "optom", "ophthalm", "eyewear", "eye", "vision", "sight"],
  },
  {
    key: "general",
    keywords: ["general", "family", "gp", "physician", "internal medicine", "medicine", "practice", "clinic", "doctor"],
  },
];

/**
 * The glyph for a category name. Unknown categories get the stethoscope, which
 * is the honest answer for "some kind of clinician" and never renders blank.
 */
export function categoryGlyph(name: string): GlyphKey {
  const normalized = name.trim().toLowerCase();
  if (normalized === "") return "general";

  // An exact name beats a partial one, so a category literally called "Eye"
  // is not out-matched by an earlier rule that merely contains it.
  for (const rule of RULES) {
    if (rule.keywords.includes(normalized)) return rule.key;
  }
  for (const rule of RULES) {
    if (rule.keywords.some((keyword) => normalized.includes(keyword))) return rule.key;
  }
  return "general";
}
